import { Node, mergeAttributes } from '@tiptap/core';

/**
 * Attributes to preserve on div elements
 */
const PRESERVED_ATTRIBUTES = ['id', 'class', 'style', 'title', 'lang', 'dir'];

/**
 * Get attributes from an element
 */
const getPreservedAttributes = (element: HTMLElement): Record<string, string> => {
  const attrs: Record<string, string> = {};

  PRESERVED_ATTRIBUTES.forEach(attr => {
    const value = element.getAttribute(attr);
    if (value !== null) {
      attrs[attr] = value;
    }
  });

  // Get data-* attributes
  Array.from(element.attributes).forEach(attr => {
    if (attr.name.startsWith('data-')) {
      attrs[attr.name] = attr.value;
    }
  });

  return attrs;
};

export interface PreserveDivOptions {
  HTMLAttributes: Record<string, string>;
}

/**
 * PreserveDiv extension for Tiptap
 *
 * Preserves div elements with id, class, or style attributes that contain other blocks.
 * This allows styled container elements from external sources (like email chains) to pass
 * through without being stripped.
 */
export const PreserveDiv = Node.create<PreserveDivOptions>({
  name: 'preserveDiv',

  group: 'block',

  content: 'block*',

  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },

  addAttributes() {
    return {
      preservedAttrs: {
        default: {},
        parseHTML: element => getPreservedAttributes(element),
        renderHTML: attributes => attributes.preservedAttrs || {}
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        priority: 30,
        getAttrs: element => {
          if (typeof element === 'string') return false;
          if (
            !element.getAttribute('id') &&
            !element.getAttribute('class') &&
            !element.getAttribute('style')
          ) {
            return false;
          }
          return null;
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  }
});
